import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type PlantType } from '@common/types/common';
import { type BoostCardProps } from '@common/types/components';
import { useAppSelector } from '@redux/store';
import cn from 'classnames';

import { divide } from '@/common/lib/decimal';
import { timeStringFormat } from '@/common/lib/timer';

import { StringSmall, StringXSmall } from '../Typography/Typography';
import { Picture } from '../ui/Picture/Picture';

import { BoostButton } from './ReciveBoostButtons/BoostButton';

import css from './styles.module.scss';

import starsPatternPng from '/img/patterns/stars-small.png';
import starsPatternWebp from '/img/patterns/stars-small.webp';

const BoostCard: FC<BoostCardProps> = ({
  boostType,
  duration,
  nextUseInSeconds,
  expiresInSeconds,
  effectMultiplier,
  quantity,
  plantType,
  plantId,
  tgem,
}) => {
  const { t } = useTranslation();
  const quarryQuantity = useAppSelector(
    (state) => state.quarry.quarries?.userQuarries.length,
  );
  const factoryQuantity = useAppSelector((state) => state.factory.list?.length);
  const workshopQuantity = useAppSelector(
    (state) => state.workshop.list?.length,
  );
  const quantityBuilds = useMemo(() => {
    const quantity: Record<PlantType, number> = {
      Quarry: quarryQuantity || 0,
      Factory: factoryQuantity || 0,
      Workshop: workshopQuantity || 0,
    };

    return quantity[plantType];
  }, [plantType, quarryQuantity, factoryQuantity, workshopQuantity]);

  return (
    <div className={css.boostCard}>
      {!!expiresInSeconds && (
        <div className={css.overlay}>
          <div
            className={cn(
              css.elipse,
              boostType === 'Free' || boostType === 'Advertising'
                ? css['elipse--green']
                : effectMultiplier === 5
                  ? css['elipse--yellow']
                  : effectMultiplier === 10
                    ? css['elipse--red']
                    : css['elipse--purple'],
            )}
          ></div>
          <div
            className={cn(
              css.gradient,
              boostType === 'Free' || boostType === 'Advertising'
                ? css['gradient--green']
                : effectMultiplier === 5
                  ? css['gradient--yellow']
                  : effectMultiplier === 10
                    ? css['gradient--red']
                    : css['gradient--purple'],
            )}
          ></div>
          <Picture
            img={starsPatternPng}
            webp={starsPatternWebp}
            className={css.mask}
            alt="star mask"
          />
        </div>
      )}
      <div className={css.boostCardContent}>
        {quantity && (
          <div className={css.adsBoostCount}>
            <StringXSmall weight="bold">{`${quantity.available}/${quantity.limit}`}</StringXSmall>
          </div>
        )}
        <span
          className={cn(
            !!expiresInSeconds &&
              (boostType === 'VIP' || boostType === 'Paid') &&
              '!text-gold',
            'text-[1.25rem] rounded-none font-black italic leading-none mb-[0.375rem]',
            {
              'text-shadow-[0_0_8px_#ffd600] text-[#fff8d4]':
                !!expiresInSeconds && effectMultiplier === 5,
              'text-shadow-[0_0_8px_#ff5858] text-[#ffe6e6]':
                !!expiresInSeconds && effectMultiplier === 10,
            },
          )}
        >
          x{effectMultiplier}
        </span>
        <div className={cn(css.boostTime)}>
          {expiresInSeconds ? (
            <StringSmall weight="medium">
              {timeStringFormat(expiresInSeconds * 1000)}
            </StringSmall>
          ) : boostType === 'BoostForCrypto' ? (
            <StringSmall weight="medium">{`${effectMultiplier.toString()} ${t('min')}`}</StringSmall>
          ) : (
            <StringSmall weight="medium">{`${duration ? divide(duration, 60) : 2} ${t(duration ? 'min' : 'hours')}`}</StringSmall>
          )}
        </div>
        <BoostButton
          kind={boostType}
          nextUseInSeconds={nextUseInSeconds}
          tgemBoostX5X10={tgem}
          plantId={plantId}
          plantType={plantType}
          effectMultiplier={effectMultiplier}
          tgemCost={boostType === 'VIP' ? quantityBuilds * 5 * 0.8 : 5}
          duration={duration}
        />
      </div>
    </div>
  );
};

export { BoostCard };
